/**
 * MessagingQuotaBanner · iter138
 *
 * Zeigt im Chat-Kontext das aktuelle Nachrichten-Kontingent des Users
 * gegenüber einem Creator + kaufbare Pakete + Tip-Fallback.
 *
 * API:
 *  - GET /api/messaging/quota/{creator_id}
 *  - POST /api/messaging/packages/{creator_id}/{package_index}/purchase
 *
 * Props:
 *  - creator: {person_id|_id, display_name, handle}
 *  - onQuotaChange?(quota): callback für Parent (z.B. Send-Button disabled)
 */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Loader2, ShoppingBag, Info, Heart } from "lucide-react";
import api from "@/lib/api";
import TipButton from "@/components/TipButton";

export default function MessagingQuotaBanner({ creator, onQuotaChange }) {
  const { t } = useTranslation();
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyIdx, setBusyIdx] = useState(null);

  const cid = creator?.person_id || creator?._id || creator?.id;

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/messaging/quota/${cid}`);
      setQuota(data);
      onQuotaChange && onQuotaChange(data);
    } catch (e) {
      setQuota({ error: e?.response?.data?.detail || "load_error" });
    } finally { setLoading(false); }
  }, [cid, onQuotaChange]);

  useEffect(() => { load(); }, [load]);

  const purchase = async (idx) => {
    setBusyIdx(idx);
    try {
      const { data } = await api.post(`/messaging/packages/${cid}/${idx}/purchase`, {
        origin_url: window.location.origin,
      });
      if (data?.url) { window.location.href = data.url; return; }
    } catch (e) {
      alert(e?.response?.data?.detail || t("msg_quota.purchase_error", { defaultValue: "Kauf konnte nicht gestartet werden." }));
    } finally { setBusyIdx(null); }
  };

  if (!cid || loading) return null;
  if (quota?.unlimited) return null; // free_for_all oder self → keine Anzeige

  const pkgs = quota?.packages_available_for_purchase || [];
  const totalRemaining = Number(quota?.total_remaining || 0);
  const tierUsed = Number(quota?.tier_quota_used || 0);
  const tierTotal = Number(quota?.tier_quota_total || 0);
  const pkgCredits = Number(quota?.package_credits || 0);
  const emptyAndNoPkgs = totalRemaining === 0 && pkgs.length === 0;

  const chipCls = totalRemaining === 0
    ? "border-red-200 bg-red-50 text-red-700"
    : totalRemaining <= 3 ? "border-amber-200 bg-amber-50 text-amber-700"
    : "border-emerald-200 bg-emerald-50 text-emerald-700";

  return (
    <div data-testid="msg-quota-banner" className="rounded-2xl border border-[#E5E5E5] bg-white p-3 my-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="msg-quota-total"
              className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full border px-2.5 py-1 ${chipCls}`}>
          <MessageCircle size={12} />
          {t("msg_quota.remaining", { count: totalRemaining, defaultValue: "{{count}} Nachrichten übrig" })}
        </span>
        {tierTotal > 0 && (
          <span data-testid="msg-quota-tier" className="text-[10px] text-[#666]">
            {t("msg_quota.tier_line", { defaultValue: "Abo: {{used}}/{{total}}", used: tierUsed, total: tierTotal })}
          </span>
        )}
        {pkgCredits > 0 && (
          <span data-testid="msg-quota-pkg" className="text-[10px] text-[#666]">
            {t("msg_quota.pkg_line", { defaultValue: "Pakete: {{n}}", n: pkgCredits })}
          </span>
        )}
      </div>

      {/* Erschöpft + keine Pakete → Tip-Fallback */}
      {emptyAndNoPkgs && (
        <div className="mt-3 rounded-xl bg-[#FAFAFA] border border-[#E5E5E5] p-2.5 flex items-start gap-2 text-xs text-[#666]">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p>{t("msg_quota.exhausted_no_pkg", { defaultValue: "Dein Kontingent ist erschöpft. Der Creator bietet aktuell keine Pakete an — gib doch ein Trinkgeld." })}</p>
          </div>
          <TipButton creator={creator} source="CHAT" variant="chip" size="sm"
            label={t("msg_quota.tip", { defaultValue: "Trinkgeld" })}
            dataTestid="msg-quota-tip-btn" />
        </div>
      )}

      {/* Pakete kaufbar */}
      {pkgs.length > 0 && (
        <div className="mt-3">
          {totalRemaining === 0 && (
            <p className="text-xs font-bold text-red-700 mb-2">
              {t("msg_quota.buy_prompt", { defaultValue: "Kontingent erschöpft — kaufe ein Paket, um weiterzuschreiben." })}
            </p>
          )}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pkgs.map((p, i) => (
              <li key={i} data-testid={`msg-quota-pkg-item-${i}`} className="rounded-2xl border border-[#E5E5E5] p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-[#0A0A0A] truncate">{p.label || `Paket ${i + 1}`}</p>
                  <p className="text-[10px] text-[#666]">
                    {t("msg_quota.pkg_x_msgs", { defaultValue: "{{n}} Nachrichten", n: p.messages })}
                  </p>
                </div>
                <button
                  data-testid={`msg-quota-buy-${i}`}
                  onClick={() => purchase(i)}
                  disabled={busyIdx === i}
                  className="h-9 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-bold flex items-center gap-1.5 hover:bg-[#222] disabled:opacity-40 shrink-0">
                  {busyIdx === i ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                  {Number(p.price_eur).toFixed(2)} €
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
